import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api/client";
import { paymentsApi } from "@/lib/api/endpoints";
import { DEMO } from "@/lib/api/demo";
import { Spinner } from "@/components/ui";

/**
 * Where the guest's phone lands after paying.
 *
 * This is the provider's return URL, so it opens on the guest's own handset —
 * not on the kiosk. The kiosk is separately polling the order and will move
 * itself on, which is why this page only has to reassure the person holding the
 * phone, not drive the booth.
 *
 * In demo mode the QR points here too, and arriving settles the order — that
 * stands in for telebirr so the whole scan-and-pay flow can be walked on one
 * machine with no merchant account.
 */
export function PaymentReturnPage() {
  const { reference = "" } = useParams<{ reference: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<"working" | "pending" | "paid" | "failed" | "error">("working");

  useEffect(() => {
    let cancelled = false;

    const returnToPhoto = (token: string, result: "paid" | "failed") => {
      window.setTimeout(() => {
        if (!cancelled) navigate(`/s/${token}?payment=${result}&action=download`);
      }, result === "paid" ? 900 : 500);
    };

    async function run() {
      try {
        if (DEMO) {
          await api.post(`/api/demo/pay/${reference}`);
          if (!cancelled) setState("paid");
          return;
        }

        for (let attempt = 0; attempt < 40; attempt += 1) {
          const order = await paymentsApi.order(reference);
          if (cancelled) return;

          if (order.paid) {
            setState("paid");
            if (order.public_token) {
              returnToPhoto(order.public_token, "paid");
            }
            return;
          }

          if (order.status === "failed" || order.status === "expired") {
            setState("failed");
            if (order.public_token) returnToPhoto(order.public_token, "failed");
            return;
          }

          setState("pending");
          await new Promise((resolve) => window.setTimeout(resolve, 1500));
        }
      } catch {
        if (!cancelled) setState("error");
      }
    }

    void run();
    return () => { cancelled = true; };
  }, [reference, navigate]);

  return (
    <div className="grid min-h-full place-items-center bg-ink-850 px-6 py-16 text-center">
      <div className="flex max-w-sm flex-col items-center gap-4">
        {state === "working" && <Spinner size={28} className="text-accent" />}

        {state === "paid" && (
          <>
            <div className="text-6xl">✅</div>
            <h1 className="text-xl font-semibold">Payment received</h1>
            <p className="text-sm leading-relaxed text-haze-300">
              Your photo is unlocking on the booth now. You can put your phone away.
            </p>
          </>
        )}

        {state === "pending" && (
          <>
            <div className="text-6xl">⏳</div>
            <h1 className="text-xl font-semibold">Almost there</h1>
            <p className="text-sm leading-relaxed text-haze-300">
              We haven't seen the payment confirmed yet. If you've just approved it, the booth will
              catch up within a few seconds.
            </p>
          </>
        )}

        {state === "failed" && (
          <>
            <div className="text-6xl">❌</div>
            <h1 className="text-xl font-semibold">Payment was not completed</h1>
            <p className="text-sm leading-relaxed text-haze-300">
              No payment was confirmed. You can return to your photo and try again.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <div className="text-6xl">🤔</div>
            <h1 className="text-xl font-semibold">We couldn't find that payment</h1>
            <p className="text-sm leading-relaxed text-haze-300">
              Nothing has been charged. Speak to a member of staff at the booth.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
